import React from 'react';
import { Cloud, Building2, Network, Shield, Users, Package, Truck, User, ArrowUp, ArrowDown, Eye, BarChart3, Bell } from 'lucide-react';
export function ArchitectureSlide() {
  const features = [{
    icon: <Cloud className="w-5 h-5 text-[#2EBCB0]" data-id="element-45" />,
    title: 'Cloud-Based Platform',
    desc: 'Scalable, secure, and centrally managed'
  }, {
    icon: <Building2 className="w-5 h-5 text-[#2EBCB0]" data-id="element-46" />,
    title: 'Multi-Tenant Architecture',
    desc: 'Supports multiple companies and entities within a single system'
  }, {
    icon: <Network className="w-5 h-5 text-[#2EBCB0]" data-id="element-47" />,
    title: 'Nested User Hierarchy',
    desc: 'Enables structured control across organizations, subsidiaries, fleets, and drivers'
  }];
  const authorities = [{
    name: 'Transport Authority',
    icon: <Building2 className="w-4 h-4 text-gray-500" data-id="element-48" />
  }, {
    name: 'Police',
    icon: <Shield className="w-4 h-4 text-gray-500" data-id="element-49" />
  }, {
    name: 'Other Authorities',
    icon: <Users className="w-4 h-4 text-gray-500" data-id="element-50" />
  }];
  const companies = [{
    name: 'DHL',
    icon: <Package className="w-4 h-4 text-gray-500" data-id="element-51" />
  }, {
    name: 'Amazon',
    icon: <Package className="w-4 h-4 text-gray-500" data-id="element-52" />
  }, {
    name: 'Easylease',
    icon: <Building2 className="w-4 h-4 text-gray-500" data-id="element-53" />
  }];
  // Helper component for vertical sub-structure items
  const SubItem = ({
    name,
    icon,
    isLast = false
  }: {
    name: string;
    icon: React.ReactNode;
    isLast?: boolean;
  }) => <div className="flex flex-col items-center w-full" data-id="element-54">
      {/* Vertical animated line up */}
      <div className="h-6 w-px relative" data-id="element-55">
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" data-id="element-56">
          <path d="M 0.5 0 L 0.5 100" fill="none" stroke="#2EBCB0" strokeWidth="1.5" opacity="0.2" data-id="element-57" />
          <path d="M 0.5 0 L 0.5 100" fill="none" stroke="#2EBCB0" strokeWidth="2" strokeDasharray="4 6" className="flow-up" opacity="0.8" data-id="element-58" />
        </svg>
      </div>
      {/* Item Box */}
      <div className="bg-white border border-gray-200 rounded-lg py-1.5 px-4 shadow-sm flex items-center gap-2 w-32 justify-center z-10" data-id="element-59">
        {icon}
        <span className="text-xs font-medium text-gray-700" data-id="element-60">{name}</span>
      </div>
    </div>;
  return <div className="flex flex-col w-screen h-screen overflow-hidden bg-white text-[#1a1a2e] px-12 py-8" data-id="element-61">
      {/* Top Header */}
      <div className="flex justify-between items-start shrink-0" data-id="element-62">
        <img src="https://cdn.magicpatterns.com/uploads/wGUVVF9ppu2J6VxJ2kpoN3/Pair2_Logo.png" alt="Pair2 Logo" className="h-10 w-auto object-contain" data-id="element-63" />
      </div>

      {/* Title & Features */}
      <div className="text-center mt-2 mb-6 shrink-0" data-id="element-64">
        <span className="text-sm font-semibold text-[#2EBCB0] uppercase tracking-widest" data-id="element-65">
          System Architecture
        </span>
        <div className="mb-6" data-id="element-66"></div>

        <div className="flex justify-center gap-8 max-w-5xl mx-auto" data-id="element-67">
          {features.map((feat, idx) => <div key={idx} className="flex items-center gap-2" data-id="element-68">
              <div className="bg-[#2EBCB0]/10 p-1.5 rounded-lg" data-id="element-69">
                {feat.icon}
              </div>
              <h3 className="font-semibold text-[0.875rem] text-[#1a1a2e]" data-id="element-70">
                {feat.title}
              </h3>
            </div>)}
        </div>
      </div>

      {/* Main Visual: Hierarchical Tree with Side Panels */}
      <div className="flex-grow flex items-stretch w-full relative mt-4" data-id="element-71">
        {/* Left Side Panel */}
        <div className="w-48 shrink-0 flex flex-col justify-center pr-6" data-id="element-72">
          <div className="bg-[#2EBCB0]/5 border border-[#2EBCB0]/15 rounded-xl p-4" data-id="element-73">
            <h4 className="text-xs font-bold text-[#2EBCB0] uppercase tracking-wider mb-3" data-id="element-74">
              Operational Data Flow
            </h4>
            <ul className="space-y-2.5" data-id="element-75">
              {[{
              name: 'Driver Identity',
              icon: <User className="w-3.5 h-3.5 text-[#2EBCB0]" data-id="element-76" />
            }, {
              name: 'Vehicle Info',
              icon: <Truck className="w-3.5 h-3.5 text-[#2EBCB0]" data-id="element-77" />
            }, {
              name: 'Tracking',
              icon: <ArrowUp className="w-3.5 h-3.5 text-[#2EBCB0]" data-id="element-78" />
            }, {
              name: 'Violations',
              icon: <Shield className="w-3.5 h-3.5 text-[#2EBCB0]" data-id="element-79" />
            }].map((item, i) => <li key={i} className="flex items-center gap-2" data-id="element-80">
                  {item.icon}
                  <span className="text-xs text-gray-600 font-medium" data-id="element-81">
                    {item.name}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Center Tree */}
        <div className="flex-grow flex flex-col items-center justify-start max-w-5xl mx-auto w-full relative" data-id="element-82">
          {/* LEVEL 1: Authorities */}
          <div className="w-full flex flex-col items-center z-10" data-id="element-83">
            <span className="text-[0.65rem] font-bold text-gray-400 uppercase tracking-widest mb-2" data-id="element-84">
              Authorities
            </span>
            <div className="flex justify-between w-full max-w-2xl px-8" data-id="element-85">
              {authorities.map((auth, idx) => <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl py-2 px-5 shadow-sm flex items-center gap-2 w-40 justify-center" data-id="element-86">
                  {auth.icon}
                  <span className="text-sm font-semibold text-gray-700" data-id="element-87">
                    {auth.name}
                  </span>
                </div>)}
            </div>
          </div>

          {/* CONNECTIONS: Authorities <-> Pair2 */}
          <div className="w-full max-w-2xl h-12 relative shrink-0" data-id="element-88">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-89">
              {/* Left branch (Transport to Pair2) */}
              <path d="M 15 0 C 15 50, 50 50, 50 100" fill="none" stroke="#2EBCB0" strokeWidth="0.5" opacity="0.2" data-id="element-90" />
              <path d="M 15 0 C 15 50, 50 50, 50 100" fill="none" stroke="#2EBCB0" strokeWidth="1" strokeDasharray="2 3" className="flow-up" opacity="0.8" data-id="element-91" />

              {/* Center branch (Police to Pair2) */}
              <path d="M 50 0 L 50 100" fill="none" stroke="#2EBCB0" strokeWidth="0.5" opacity="0.2" data-id="element-92" />
              <path d="M 50 0 L 50 100" fill="none" stroke="#2EBCB0" strokeWidth="1" strokeDasharray="2 3" className="flow-up" opacity="0.8" data-id="element-93" />

              {/* Right branch (Other to Pair2) */}
              <path d="M 85 0 C 85 50, 50 50, 50 100" fill="none" stroke="#2EBCB0" strokeWidth="0.5" opacity="0.2" data-id="element-94" />
              <path d="M 85 0 C 85 50, 50 50, 50 100" fill="none" stroke="#2EBCB0" strokeWidth="1" strokeDasharray="2 3" className="flow-up" opacity="0.8" data-id="element-95" />
            </svg>
            {/* Flow direction labels */}
            <div className="absolute top-1/2 left-[30%] -translate-y-1/2 bg-white px-1 text-[0.5rem] text-[#2EBCB0] font-bold flex items-center gap-0.5" data-id="element-96">
              <ArrowUp className="w-2 h-2" data-id="element-97" /> REPORTING
            </div>
            <div className="absolute top-1/2 right-[30%] -translate-y-1/2 bg-white px-1 text-[0.5rem] text-[#2EBCB0] font-bold flex items-center gap-0.5" data-id="element-98">
              CONTROL <ArrowDown className="w-2 h-2" data-id="element-99" />
            </div>
          </div>

          {/* LEVEL 2: Pair2 Platform */}
          <div className="z-20 relative" data-id="element-100">
            <div className="bg-white border-2 border-[#2EBCB0] rounded-2xl shadow-lg px-10 py-5 flex flex-col items-center relative overflow-hidden" data-id="element-101">
              <div className="absolute left-0 top-0 bottom-0 w-2 bg-[#2EBCB0]" data-id="element-102"></div>
              <img src="https://cdn.magicpatterns.com/uploads/wGUVVF9ppu2J6VxJ2kpoN3/Pair2_Logo.png" alt="Pair2" className="h-8 mb-1" data-id="element-103" />
              <h2 className="text-xl font-bold text-[#1a1a2e]" data-id="element-104">
                National Platform
              </h2>
            </div>
          </div>

          {/* CONNECTIONS: Pair2 <-> Companies */}
          <div className="w-full max-w-3xl h-12 relative shrink-0" data-id="element-105">
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-106">
              {/* Left branch (Pair2 to DHL) */}
              <path d="M 50 0 C 50 50, 16.6 50, 16.6 100" fill="none" stroke="#2EBCB0" strokeWidth="0.5" opacity="0.2" data-id="element-107" />
              <path d="M 50 0 C 50 50, 16.6 50, 16.6 100" fill="none" stroke="#2EBCB0" strokeWidth="1" strokeDasharray="2 3" className="flow-up" opacity="0.8" data-id="element-108" />

              {/* Center branch (Pair2 to Amazon) */}
              <path d="M 50 0 L 50 100" fill="none" stroke="#2EBCB0" strokeWidth="0.5" opacity="0.2" data-id="element-109" />
              <path d="M 50 0 L 50 100" fill="none" stroke="#2EBCB0" strokeWidth="1" strokeDasharray="2 3" className="flow-up" opacity="0.8" data-id="element-110" />

              {/* Right branch (Pair2 to Easylease) */}
              <path d="M 50 0 C 50 50, 83.3 50, 83.3 100" fill="none" stroke="#2EBCB0" strokeWidth="0.5" opacity="0.2" data-id="element-111" />
              <path d="M 50 0 C 50 50, 83.3 50, 83.3 100" fill="none" stroke="#2EBCB0" strokeWidth="1" strokeDasharray="2 3" className="flow-up" opacity="0.8" data-id="element-112" />
            </svg>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-[0.55rem] text-[#2EBCB0] font-bold flex items-center gap-1 border border-[#2EBCB0]/20 rounded-full" data-id="element-113">
              <ArrowUp className="w-2.5 h-2.5" data-id="element-114" /> OPERATIONAL DATA FLOW
            </div>
          </div>

          {/* LEVEL 3 & 4: Companies & Sub-structure */}
          <div className="w-full flex flex-col items-center z-10" data-id="element-115">
            <span className="text-[0.65rem] font-bold text-gray-400 uppercase tracking-widest mb-2" data-id="element-116">
              Companies / Operators
            </span>

            <div className="flex justify-between w-full max-w-3xl" data-id="element-117">
              {/* DHL Column */}
              <div className="flex flex-col items-center w-1/3" data-id="element-118">
                <div className="bg-white border border-gray-200 rounded-xl py-2 px-6 shadow-sm flex items-center gap-2 w-36 justify-center z-10" data-id="element-119">
                  <Package className="w-4 h-4 text-gray-500" data-id="element-120" />
                  <span className="text-sm font-bold text-[#1a1a2e]" data-id="element-121">DHL</span>
                </div>
                <SubItem name="Vehicles" icon={<Truck className="w-3.5 h-3.5 text-gray-500" data-id="element-123" />} data-id="element-122" />
                <SubItem name="Drivers" icon={<User className="w-3.5 h-3.5 text-gray-500" data-id="element-125" />} isLast={true} data-id="element-124" />
              </div>

              {/* Amazon Column */}
              <div className="flex flex-col items-center w-1/3" data-id="element-126">
                <div className="bg-white border border-gray-200 rounded-xl py-2 px-6 shadow-sm flex items-center gap-2 w-36 justify-center z-10" data-id="element-127">
                  <Package className="w-4 h-4 text-gray-500" data-id="element-128" />
                  <span className="text-sm font-bold text-[#1a1a2e]" data-id="element-129">
                    Amazon
                  </span>
                </div>
                <SubItem name="Vehicles" icon={<Truck className="w-3.5 h-3.5 text-gray-500" data-id="element-131" />} data-id="element-130" />
                <SubItem name="Drivers" icon={<User className="w-3.5 h-3.5 text-gray-500" data-id="element-133" />} isLast={true} data-id="element-132" />
              </div>

              {/* Easylease Column (Deep nested) */}
              <div className="flex flex-col items-center w-1/3" data-id="element-134">
                <div className="bg-white border border-gray-200 rounded-xl py-2 px-6 shadow-sm flex items-center gap-2 w-36 justify-center z-10" data-id="element-135">
                  <Building2 className="w-4 h-4 text-gray-500" data-id="element-136" />
                  <span className="text-sm font-bold text-[#1a1a2e]" data-id="element-137">
                    Easylease
                  </span>
                </div>
                <SubItem name="Uplift" icon={<Building2 className="w-3.5 h-3.5 text-gray-500" data-id="element-139" />} data-id="element-138" />
                <SubItem name="Talabat" icon={<Package className="w-3.5 h-3.5 text-gray-500" data-id="element-141" />} data-id="element-140" />
                <SubItem name="Vehicles" icon={<Truck className="w-3.5 h-3.5 text-gray-500" data-id="element-143" />} data-id="element-142" />
                <SubItem name="Drivers" icon={<User className="w-3.5 h-3.5 text-gray-500" data-id="element-145" />} isLast={true} data-id="element-144" />
              </div>
            </div>
          </div>
        </div>

        {/* Right Side Panel */}
        <div className="w-48 shrink-0 flex flex-col justify-center pl-6" data-id="element-146">
          <div className="bg-[#2EBCB0]/5 border border-[#2EBCB0]/15 rounded-xl p-4" data-id="element-147">
            <h4 className="text-xs font-bold text-[#2EBCB0] uppercase tracking-wider mb-3" data-id="element-148">
              Control & Intelligence
            </h4>
            <ul className="space-y-2.5" data-id="element-149">
              {[{
              name: 'Monitoring',
              icon: <Eye className="w-3.5 h-3.5 text-[#2EBCB0]" data-id="element-150" />
            }, {
              name: 'Compliance',
              icon: <Shield className="w-3.5 h-3.5 text-[#2EBCB0]" data-id="element-151" />
            }, {
              name: 'Alerts',
              icon: <Bell className="w-3.5 h-3.5 text-[#2EBCB0]" data-id="element-152" />
            }, {
              name: 'Analytics',
              icon: <BarChart3 className="w-3.5 h-3.5 text-[#2EBCB0]" data-id="element-153" />
            }].map((item, i) => <li key={i} className="flex items-center gap-2" data-id="element-154">
                  {item.icon}
                  <span className="text-xs text-gray-600 font-medium" data-id="element-155">
                    {item.name}
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </div>;
}